import { useTranslation } from "next-i18next";

import { useAskSellerQuestion } from "@/Apollo/hooks/catalog/mutations/useAskSellerQuestion";
import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { useGuestModalGuard } from "@/common/hooks/guest.hooks";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { useVerifyAccountModalGuard } from "@/features/verify-account/hooks/useVerifyAccountModalGuard";
import addToast from "@/toast/addToast";
import { showModal } from "@whoppah/ui/Modal2";

export type AskSellerDraft = {
  message: string;
  /** Shows the "sent once your phone is verified" line. */
  pendingVerification?: boolean;
};

/**
 * The "Contact seller" flow: the message popup, the login wall, phone verification, the send and
 * the go-to-chat confirmation.
 * @param product - What the message is about.
 * @param send - Sends the message and resolves the chat thread id. Default: `askProductQuestion`
 * on `product.id`. Open Home pieces pass their own, which keeps the product id on the server.
 */
export const useAskSeller = (
  product: Pick<ProductBySlugNonNullable, "id" | "title">,
  send?: (message: string) => Promise<string | null | undefined>
) => {
  const { t } = useTranslation();
  const { guestGuard } = useGuestModalGuard();
  const { guard: verifyGuard } = useVerifyAccountModalGuard();
  const [askSellerQuestion] = useAskSellerQuestion();

  /**
   * WHO-6172: whatever stops the send re-opens the popup with the message kept.
   * @param draft - The message to show again.
   * @param loggedIn - Skips the login wall on a re-open. `guestGuard` still
   * sees the pre-login render and would show it again.
   */
  const openAskSeller = async (
    draft?: AskSellerDraft,
    loggedIn = false
  ): Promise<void> => {
    const payload = await showModal("ask-seller", draft);

    if (!payload) {
      return;
    }

    /** Not logged-in. */
    const pass =
      loggedIn ||
      (await guestGuard({
        description: t("pdp:pdp-signup-login-wall-initial-desc", {
          productTitle: product.title,
        }),
        referrer: "contact_seller",
      }));

    if (!pass) {
      return;
    }

    /** If the user is not verified, we will show verification guard. */
    const verified = await verifyGuard();

    if (!verified) {
      return openAskSeller(
        {
          message: payload.message,
          pendingVerification: verified === false,
        },
        true
      );
    }

    try {
      const threadId = send
        ? await send(payload.message)
        : (
            await askSellerQuestion({
              variables: {
                id: product.id,
                body: payload.message,
              },
            })
          ).data?.askProductQuestion?.id;

      /** Trigger `contact_seller_message_sent_pdp` event */
      analyticsService.trackContactSellerMessageSentPdp();

      /** Show confirmation modal. */
      await showModal("ask-seller-confirmation", {
        threadId,
      });
    } catch (ex) {
      console.error(ex);

      addToast(t("common:common_generic_error_message"), {
        appearance: "error",
      });

      return openAskSeller({ message: payload.message }, true);
    }
  };

  const askSeller = async () => {
    /** Trigger `contact_seller_pdp` event. */
    analyticsService.trackContactSellerPdp();

    await openAskSeller();
  };

  return { askSeller };
};
